import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";

import { useSession } from "../../hooks/useSession.js";
import { joinParty } from "../../services/partyService.js";

/**
 * The last step before a seat — one tap, for somebody this installation admits.
 *
 * **No name field, and no anonymous path.** ADR 0027 (phase 20f-1) reverses ADR
 * 0007's ten-second join: a player needs a session on an approved account, and
 * the server already knows what to call them, so asking would be asking a
 * question we have the answer to (finding 1.4, dissolved rather than fixed).
 *
 * A code-holder with no session is sent to **`/login?code=…`**, not to an
 * apology and not to `/register`: the common case by far is somebody who
 * already has an account clicking a link a friend pasted into a chat, and
 * `/login` names the party they are reaching for and returns them to it (20f-2).
 * Registering is one link further on, and it does not end in this party tonight
 * — a new account waits for an admin (19a), which is the cost ADR 0027 accepted.
 *
 * 🔴 **A television does not come through here.** `/display` joins on the room
 * code alone (`useBoard`, ADR 0005) and never renders this panel; the account
 * below is a *player's*, and a screen is not a person.
 */
export function JoinPanel({
  code,
  onJoined,
}: {
  /** The four-character room code, from the URL. */
  code: string;
  /** Called once a seat exists, so the page can open its socket. */
  onJoined: () => void;
}) {
  const { t } = useTranslation();
  const { account } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      await joinParty({ code });
      onJoined();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("join.failed"));
      setBusy(false);
    }
  };

  // Three states, not two. Rendering the redirect while the answer is still in
  // flight would bounce somebody who *is* signed in out to the register page and
  // back, which reads as the app losing their account.
  if (account === undefined) {
    return (
      <main className="flex min-h-full items-center justify-center p-6">
        <p className="text-sm text-white/50">{t("join.checking")}</p>
      </main>
    );
  }

  if (account === null) {
    return <Navigate to={`/login?code=${encodeURIComponent(code)}`} replace />;
  }

  // An account always has the profile signup made alongside it, but
  // `noUncheckedIndexedAccess` is right that this file cannot prove it — and the
  // address is a true answer to "who are you about to play as".
  const you = account.profiles[0]?.display_name ?? account.email;

  return (
    <main className="flex min-h-full flex-col items-center justify-center gap-6 p-6">
      <header className="text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-flash-500">
          {t("app.name")}
        </p>
        <h1 className="mt-1 text-3xl font-semibold tabular-nums tracking-widest">
          {code}
        </h1>
      </header>

      <div className="flex w-full max-w-sm flex-col gap-3">
        <p className="text-center text-sm text-white/70">{t("join.as", { name: you })}</p>

        {error && (
          <p role="alert" className="text-sm text-wrong-500">
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => {
            void join();
          }}
          className="min-h-14 rounded-lg bg-flash-500 px-4 text-lg font-semibold text-stage-950 disabled:opacity-40"
        >
          {busy ? t("join.joining") : t("join.action")}
        </button>
      </div>
    </main>
  );
}
